import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import OrganizationList from "../../../components/Admin/Organizations/OrganizationList";
import OrganizationForm, { type OrganizationFormValues } from "../../../components/Admin/Organizations/OrganizationForm";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getListAllOrganizationQueryOptions,
  getCreateOrganizationMutationOptions,
  getEditOrganizationMutationOptions,
  getEnableTenantOrganizationMutationOptions,
  getDisableTenantOrganizationMutationOptions,
  getOrganization
} from "../../../api/bpm/endpoints";
import type { OrganizationDto } from "../../../api/bpm/model";
import OrganizationFilters from "../../../components/Admin/Organizations/OrganizationFilters";
import SmtpDialog from "../../../components/Admin/Smtp/SmtpDialog";
import { useTranslation } from "react-i18next";
import { encodeNavParams } from "../../../utils/navParams";

const getOrganizationKey = (org: OrganizationDto) => org.key || org.id || "";

const organizationsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  orderBy: z.string().catch('name'),
  order: z.enum(['asc', 'desc']).catch('asc'),
});

export const Route = createFileRoute('/__auth/admin/organizations')({
  component: OrganizationsComponent,
  validateSearch: (search) => organizationsSearchSchema.parse(search),
  staticData: {
    title: "routes.organizations",
  },
});

function OrganizationsComponent() {
  const location = useLocation();
  const isSeiIntegrationRoute = /^\/admin\/organizations\/[^/]+\/sei-integration\/?$/.test(
    location.pathname,
  );

  if (isSeiIntegrationRoute) {
    return <Outlet />;
  }

  return <OrganizationsListPage />;
}

function OrganizationsListPage() {
    const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [isSmtpDialogOpen, setIsSmtpDialogOpen] = useState(false);
  const [selectedOrgForKey, setSelectedOrgForKey] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDto | null>(null);
  const [orgExtraMembers, setOrgExtraMembers] = useState<any[]>([]);
  const [loadingOrgDetails, setLoadingOrgDetails] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = "default-domain";

  const { data: orgsData, isLoading: loadingOrgs, error, refetch } = useQuery({
    ...getListAllOrganizationQueryOptions(effectiveTenant),
    enabled: !!effectiveTenant,
    staleTime: 30000,
  });
  const organizations: OrganizationDto[] = useMemo(() => (orgsData as any) || [], [orgsData]);

  const createMutation = useMutation(getCreateOrganizationMutationOptions());
  const updateMutation = useMutation(getEditOrganizationMutationOptions());
  const enableMutation = useMutation(getEnableTenantOrganizationMutationOptions());
  const disableMutation = useMutation(getDisableTenantOrganizationMutationOptions());

  const isMutationLoading = createMutation.isPending || updateMutation.isPending || enableMutation.isPending || disableMutation.isPending;

  const sortedAndFilteredOrgs = useMemo(() => {
    let result = [...organizations];

    if (search.q) {
      const query = search.q.toLowerCase();
      result = result.filter((o: any) =>
        (o.name || "").toLowerCase().includes(query) ||
        (o.key || "").toLowerCase().includes(query)
      );
    }

    const { orderBy, order } = search;
    result.sort((a: any, b: any) => {
      const valueA = a[orderBy] || '';
      const valueB = b[orderBy] || '';

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [organizations, search.q, search.orderBy, search.order]);

  const paginatedData = useMemo(() => {
    const start = (search.page - 1) * search.size;
    const end = start + search.size;
    return sortedAndFilteredOrgs.slice(start, end);
  }, [sortedAndFilteredOrgs, search.page, search.size]);

  const handleEditOrg = useCallback(async (org: OrganizationDto) => {
    setSelectedOrg(org);
    setIsOrgDialogOpen(true);
    setOrgExtraMembers([]);
    setLoadingOrgDetails(true);
    try {
      const response = await getOrganization(effectiveTenant, (org as any).key || '');
      setSelectedOrg(response as unknown as OrganizationDto);
      setOrgExtraMembers((response as any)?.extraMembers || []);
    } catch (err) {
      console.error("Error fetching org details:", err);
    } finally {
      setLoadingOrgDetails(false);
    }
  }, [effectiveTenant]);

  const handleOrgFormSubmit = async (values: OrganizationFormValues, _extraMembers?: any[]) => {
    try {
      if (selectedOrg) {
        await updateMutation.mutateAsync({
          tenant: effectiveTenant,
          organizationKey: (selectedOrg as any).key,
          data: {
            name: values.name,
            gestaoPCTTDD: values.gestaoPCTTDD,
            doczProjetoId: values.doczProjetoId,
            doczProjetoNome: values.doczProjetoNome,
            extraMembers: _extraMembers?.map((m: any) => ({ id: m.id }))
          } as any
        });
        showSnackbar(t("admin.organizations.updatedSuccess"));
      } else {
        await createMutation.mutateAsync({
          tenant: effectiveTenant,
          data: {
            key: values.id,
            name: values.name,
            gestaoPCTTDD: values.gestaoPCTTDD,
          } as any
        });
        showSnackbar(t("admin.organizations.createdSuccess"));
      }
      refetch();
      setIsOrgDialogOpen(false);
      setSelectedOrg(null);
    } catch (err: any) {
      const errorMessage = err.body?.message || err.message || t("admin.organizations.errorSaving");
      showSnackbar(errorMessage, "error");
    }
  };

  const handleToggleStatus = async (org: OrganizationDto) => {
    try {
      if (org.enabled) {
        await disableMutation.mutateAsync({
          tenant: effectiveTenant,
          organizationKey: (org as any).key || '',
        });
        showSnackbar(t("admin.organizations.inactivatedSuccess"));
      } else {
        await enableMutation.mutateAsync({
          tenant: effectiveTenant,
          organizationKey: (org as any).key || '',
        });
        showSnackbar(t("admin.organizations.activatedSuccess"));
      }
      refetch();
    } catch (err) {
      showSnackbar(t("admin.organizations.errorChangingStatus"), "error");
    }
  };

  const handleFilter = (values: { q?: string }) => {
    navigate({
      search: (prev) => ({ ...prev, ...values, page: 1 }),
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = search.orderBy === property && search.order === 'asc';
    navigate({
      search: (prev) => ({
        ...prev,
        order: isAsc ? 'desc' : 'asc',
        orderBy: property,
      }),
    });
  };

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <ListLayout
      title={t("admin.organizations.title")}
      subtitle={t("admin.organizations.subtitle")}
      isLoading={loadingOrgs}
      error={error}
      filters={
        <OrganizationFilters onFilter={handleFilter} initialValues={{ q: search.q }} isLoading={loadingOrgs} />
      }
      actions={
        <IconButton
          onClick={() => {
            setSelectedOrg(null);
            setIsOrgDialogOpen(true);
          }}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            boxShadow: 2
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      <OrganizationList
        organizations={paginatedData}
        orderBy={search.orderBy}
        order={search.order}
        onRequestSort={handleRequestSort}
        onEdit={handleEditOrg}
        onToggleStatus={handleToggleStatus}
        onConfigureEmail={(org) => {
          setSelectedOrgForKey(getOrganizationKey(org) || null);
          setIsSmtpDialogOpen(true);
        }}
        onConfigureSeiIntegration={(org) => {
          const organizationKey = getOrganizationKey(org);

          if (!organizationKey) {
            showSnackbar(t("admin.organizations.identifierMissing"), "error");
            return;
          }

          const fromParams = encodeNavParams({
            page: search.page,
            size: search.size,
            q: search.q,
            orderBy: search.orderBy,
            order: search.order,
          });

          navigate({
            to: "/admin/organizations/$organizationKey/sei-integration",
            params: { organizationKey },
            search: (() => ({ fromParams })) as any,
          });
        }}
      />

      <TablePagination
        component="div"
        count={sortedAndFilteredOrgs.length}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Dialog
        open={isOrgDialogOpen}
        onClose={() => setIsOrgDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent dividers>
          <OrganizationForm
            initialData={selectedOrg ? { 
                id: (selectedOrg as any)?.key, 
                tenant: selectedOrg.tenant || "",
                name: selectedOrg.name || "", 
                description: (selectedOrg as any)?.description, 
                gestaoPCTTDD: (selectedOrg as any)?.gestaoPCTTDD,
                doczProjetoId: (selectedOrg as any)?.doczProjetoId,
                doczProjetoNome: (selectedOrg as any)?.doczProjetoNome,
              } 
              : undefined
            }
            isEdit={!!selectedOrg}
            onSubmit={handleOrgFormSubmit}
            onCancel={() => setIsOrgDialogOpen(false)}
            isLoading={isMutationLoading}
            extraMembers={orgExtraMembers}
            loadingDetails={loadingOrgDetails}
          />
        </DialogContent>
      </Dialog>

      <SmtpDialog
        open={isSmtpDialogOpen}
        onClose={() => setIsSmtpDialogOpen(false)}
        entityType="organization"
        entityId={selectedOrgForKey || ''}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
